import PersistentSequencer from 'engine/PersistentSequencer.js';
import {padLeft, padRight, padUp, padDown} from './padArrow.js';
import ethereum from "decentralized/ethereum.js"
import {getSamplesStream} from 'search/getSamplesStream.js';
import {sortByRatings} from 'search/sortByRatings.js';
import SamplesStream from 'track/SamplesStream.js';
import EthereumSoundsSearcher from 'search/EthereumSoundsSearcher.js';
import EthereumCache from 'decentralized/cache/EthereumCache.js';
import {shuffle} from 'utils/ShuffleArray.js';
import Sample from 'repos/Sample.js';
import Cursor from 'scripting/Cursor.js';
import Resolution from 'engine/Resolution.js';
import BouncingEngine from './BouncingEngine.js';
import {
    setSelectedMidiTrack,
    setSelectedTrack,
    setSelectedEffect,
    setSelectedStep,
    setSelectedFreeze
} from 'actions/index.js';

const baseResolution = new Resolution(32, false);

class ScriptableSequencer extends PersistentSequencer {

    constructor(store, stereoOnly=false, transport, aubio, noMetronome=false, isOffline) {
        super(store, transport, aubio, stereoOnly, noMetronome);
        if (!noMetronome) {
            this.searcher  = new EthereumSoundsSearcher(new EthereumCache(store), this);
            this.searcher.cache.loadIndex();
        }
        this.cursor = new Cursor(store);
        this.stereoOnly = stereoOnly;
    }

    setScripting(scripting) {
        this.scripting = scripting;
    }
    
    clearSequence() {
        this.tracks.map((track) => track.pattern.clear());
        this.updateTracks();
    }

    getSelectedStep() {
        return this.cursor.getStep(this.resolution);
    }

    getSelectedStepData() {
        let track = this.getSelectedTrack();
        return track.getStepAtResolution(this.cursor.step, baseResolution);
    }

    getSelectedMidiTrack() {
        return this.tracks[this.state.tracks.selectedMidiTrack];
    }

    getSelectedTrack() {
        return this.cursor.track === undefined ? 0 :
            this.tracks ?
            this.tracks[this.cursor.track] : undefined;
    }

    selectMidiTrack(track) {
        this.store.dispatch(setSelectedMidiTrack(track));
    }
    selectEffect(effect) {
        this.selectedEffect = effect;
        this.dispatch(setSelectedEffect(effect.id));
    }

    selectTrack(track) {
        this.cursor.track = track;

        if (this.store.getState().mix.selectedTrack !== this.cursor.track) {
            this.store.dispatch(setSelectedTrack(track));
        }

        this.store.dispatch(setSelectedMidiTrack(track));

        if (this.tracks[track]) {
            this.tracks[track].updateSteps();
        }

        if (this.tracks[track]) {
            this.cursor.step = this.cursor.step % this.tracks[track].pattern.length();
        }
        this.store.dispatch(setSelectedStep(this.cursor.getStep(this.resolution)));

        this.store.dispatch(setSelectedFreeze(undefined));
    }

    select(track, step) {
        if (this.tracks[track] === undefined) {
            return;
        }
        this.tracks[track].stepSelected(step);
        this.cursor.setStep(step, this.resolution);
        this.cursor.track = track;
        this.store.dispatch(setSelectedTrack(track));
        this.store.dispatch(setSelectedStep(step));
    }

    selectGroupStep(group, step) {
        this.cursor.setStep(step, this.resolution);
        this.cursor.group = group;
        this.store.dispatch(setSelectedStep(step));
    }

    isSelectedTrackPad() {
        let track = this.getSelectedTrack();
        return track && track.pad !== undefined;
    }

    left(updateRedux=true) {
        if (this.tracks.length == 0) {
            return;
        }
        if (this.isSelectedTrackPad()) {
            padLeft(this);
            return;
        }
        let step = this.cursor.getStep(this.resolution);
        step--;
        if (step < 0) {
            this.up(updateRedux);
            let length = this.getTrackLength();
            this.cursor.setStep(length - 1, this.resolution);
            if (updateRedux) {
                this.store.dispatch(setSelectedTrack(this.cursor.track));
                this.store.dispatch(setSelectedStep(length - 1));
            }
            return;
        }

        this.cursor.setStep(step, this.resolution);
        this.tracks[this.cursor.track].stepSelected(this.cursor.step);
        if (updateRedux) {
            this.cursor.updateRedux();
        }
    }

    getTrackLength() {
        return this.getSelectedTrack().getStepsAtResolution(this.resolution).length;
    }

    right(updateRedux=true) {
        if (this.tracks.length == 0) {
            return;
        }
        if (this.isSelectedTrackPad()) {
            padRight(this);
            return;
        }
        let length = this.tracks[this.cursor.track].getStepsAtResolution(this.resolution).length;
        let step = this.cursor.getStep(this.resolution);
        step++;
        if (step >= length) {
            this.cursor.step = 0;
            this.down(updateRedux);
            if (updateRedux) {
                this.cursor.updateRedux();
            }
            return;
        }
        this.cursor.setStep(step, this.resolution);
        this.tracks[this.cursor.track].stepSelected(this.cursor.step);
        if (this.store.getState().mix.selectedTrack !== this.cursor.track &&
            updateRedux) {
            this.store.dispatch(setSelectedTrack(this.cursor.track));
        }
        if (updateRedux) {
            this.store.dispatch(setSelectedStep(step));
        }
    }

    setSelectedTrack(track) {
        this.store.dispatch(setSelectedTrack(track));
    }

    up(updateRedux=true) {
        if (this.tracks.length == 0) {
            return;
        }
        if (this.isSelectedTrackPad()) {
            padUp(this);
            return;
        }
        if (this.cursor.track == 0) {
            this.cursor.track = this.tracks.length - 1;
            if (this.store.getState().mix.selectedTrack !== this.cursor.track) {
                this.selectTrack(this.cursor.track);
            }
            return;
        }

        this.cursor.track--;
        if (this.store.getState().mix.selectedTrack !== this.cursor.track) {
            this.selectTrack(this.cursor.track);
        }
        this.tracks[this.cursor.track].stepSelected(this.cursor.step);
    }

    down(updateRedux=true) {
        if (this.tracks.length == 0) {
            return;
        }
        if (this.isSelectedTrackPad()) {
            padDown(this);
            return;
        }
        if (this.cursor.track == this.tracks.length - 1) {
            this.cursor.track = 0;
            this.selectTrack(this.cursor.track);
            return;
        }

        this.cursor.track++;
        if (this.store.getState().mix.selectedTrack !== this.cursor.track) {
            this.selectTrack(this.cursor.track);
        }
        this.tracks[this.cursor.track].stepSelected(this.cursor.step);
    }

    getSample(tags) {
        return new Promise(async (resolve) => {
            let guildIds = await ethereum.getGuildsWithAccess();
            let results = await getSamplesStream(tags, guildIds);
            let index = 0;
            let sampleId = results[index];
            let sample = new Sample(sampleId);
            await sample.load();
            let samplesStream = new SamplesStream(sample, this);
            samplesStream.sounds = results;
            samplesStream.initBuffers();
            resolve({
                sample,
                samplesStream
            });
        });
    }

    async searchForSounds(tags, replace, track=this.getSelectedTrack(), loadSound=true, addToPad) {
        if (!track && replace) {
            return;
        }
        let dateA = new Date().getTime();
        let guildIds = await ethereum.getGuildsWithAccess();
        let results = await getSamplesStream(tags, guildIds);
        let dateB = new Date().getTime();
        if (results.length > 0) {
            let sampleId = results[0];

            if (replace) {
                let sample = loadSound ? new Sample(sampleId) : track.sample; //new Sample(sampleId);
                let samplesStream = new SamplesStream(sample, this);
                samplesStream.tags = tags;
                samplesStream.sounds = results.slice(1); 
                if (!loadSound) {
                    track.newSampleStream(samplesStream);
                    track.updateSteps();
                } else {
                    await sample.load();
                    samplesStream.initBuffers();
                    track.auditionSound(sample, false, samplesStream);
                }
            } else {
                let sample = new Sample(sampleId);
                await sample.load();
                await this.addSound(sample, undefined, undefined, undefined, undefined, undefined, addToPad);
                let dateC = new Date().getTime();
                let time2 = dateC - dateB;
                // get samples stream
                let track = this.tracks[this.tracks.length - 1];
                track.samplesStream.tags = tags;
                track.samplesStream.sounds = results.slice(1); // shuffle([...query.unsortedResults]);
                this.scrollToSelectedTrack();
                track.updateSteps();
                setTimeout(() => {
                    track.samplesStream.initBuffers();
                }, 250);
                return track;
            }
        }
    }

    
}

export default ScriptableSequencer;
